import {
    Component,
    input,
    output,
    signal,
    effect,
    ViewChild,
    TemplateRef,
    computed,
    model,
    OnInit,
    OnDestroy,
    forwardRef,
    inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { AutoCompleteModule } from 'primeng/autocomplete';
import {
    AutoCompleteOption,
    AutoCompleteGroup,
    AutoCompleteConfig,
    AutoCompleteSearchEvent,
    AutoCompleteSelectEvent,
    AutoCompleteUnselectEvent,
    AutoCompleteChangeEvent,
    AutoCompleteClearEvent,
    AutoCompleteDropdownClickEvent,
} from './autocomplete.model';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { TextCasePipe } from '../../../../@shared/pipes/textCase.pipe';

@Component({
    selector: 'app-autocomplete',
    standalone: true,
    imports: [CommonModule, FormsModule, AutoCompleteModule],
    templateUrl: './autocomplete.component.html',
    styleUrls: ['./autocomplete.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => AutoCompleteComponent),
            multi: true,
        },
        TextCasePipe
    ],
})
export class AutoCompleteComponent implements OnInit, OnDestroy, ControlValueAccessor {
    // Model binding for selected value(s)
    value = model<any>(null);

    // Input signals for configuration
    suggestions = input<AutoCompleteOption[] | AutoCompleteGroup[]>([]);
    config = input<AutoCompleteConfig>({});
    optionLabel = input<string>('label');
    optionValue = input<string>('value');
    optionDisabled = input<string>('disabled');
    dataKey = input<string | undefined>(undefined);

    // Custom templates
    itemTemplate = input<TemplateRef<any> | null>(null);
    selectedItemTemplate = input<TemplateRef<any> | null>(null);
    headerTemplate = input<TemplateRef<any> | null>(null);
    footerTemplate = input<TemplateRef<any> | null>(null);
    emptyTemplate = input<TemplateRef<any> | null>(null);
    groupTemplate = input<TemplateRef<any> | null>(null);
    loaderTemplate = input<TemplateRef<any> | null>(null);
    dropdownIconTemplate = input<TemplateRef<any> | null>(null);
    removeIconTemplate = input<TemplateRef<any> | null>(null);
    clearIconTemplate = input<TemplateRef<any> | null>(null);

    // Output events
    onComplete = output<AutoCompleteSearchEvent>();
    onSelect = output<AutoCompleteSelectEvent>();
    onUnselect = output<AutoCompleteUnselectEvent>();
    onChange = output<AutoCompleteChangeEvent>();
    onFocus = output<void>();
    onBlur = output<void>();
    onKeyUp = output<KeyboardEvent>();
    onShow = output<void>();
    onHide = output<void>();
    onClear = output<AutoCompleteClearEvent>();
    onDropdownClick = output<AutoCompleteDropdownClickEvent>();
    onLazyLoad = output<any>();

    // Internal state
    searchQuery = signal<string>('');
    /**
     * Internal loading state for operations
     * Combined with config.loading to provide overall loading state
     */
    internalLoading = signal<boolean>(false);

    // PrimeNG AutoComplete reference
    @ViewChild('autoCompleteComponent') autoCompleteComponent: any;

    // Subjects for reactive programming
    private searchSubject = new Subject<string>();
    private destroy$ = new Subject<void>();

    // ControlValueAccessor implementation
    private onChangeFn: (value: any) => void = () => { };
    private onTouchedFn: () => void = () => { };

    // Inject TextCasePipe
    private textCasePipe = inject(TextCasePipe);

    // Computed values
    autoCompleteConfig = computed(() => {
        const defaultConfig: AutoCompleteConfig = {
            placeholder: 'Search...',
            disabled: false,
            loading: false,
            dropdown: false,
            multiple: false,
            minLength: 1,
            delay: 300,
            forceSelection: false,
            autoHighlight: true,
            showEmptyMessage: true,
            emptyMessage: 'No results found',
            scrollHeight: '200px',
            appendTo: null,
            group: false,
            optionGroupLabel: 'label',
            optionGroupChildren: 'items',
            completeOnFocus: false,
            autofocus: false,
            virtualScroll: false,
            virtualScrollItemSize: 43,
            lazy: false,
            readonly: false,
            maxlength: undefined,
            size: undefined,
            inputId: undefined,
            inputStyleClass: undefined,
            panelStyleClass: undefined,
            overlayOptions: undefined,
            dropdownMode: 'blank',
            unique: true,
            showClear: false,
        };
        return { ...defaultConfig, ...this.config() };
    });

    // Computed transformed text values using textCase pipe
    transformedPlaceholder = computed(() => {
        const config = this.autoCompleteConfig();
        const placeholder = config.placeholder || 'Search...';
        return this.textCasePipe.transform(placeholder);
    });

    transformedEmptyMessage = computed(() => {
        const config = this.autoCompleteConfig();
        const emptyMessage = config.emptyMessage || 'No results found';
        return this.textCasePipe.transform(emptyMessage);
    });

    transformedOptionGroupLabel = computed(() => {
        const config = this.autoCompleteConfig();
        return config.optionGroupLabel || 'label';
    });

    /**
     * Computed loading state - combines config.loading and internal loading
     * This provides a single source of truth for the loading state
     * - config.loading: Controlled by parent component (e.g., when fetching data)
     * - internalLoading: Controlled internally (e.g., for lazy loading)
     */
    isLoading = computed(() => {
        return this.autoCompleteConfig().loading || this.internalLoading();
    });

    constructor() {
        // Setup search debouncing
        this.searchSubject
            .pipe(
                debounceTime(this.autoCompleteConfig().delay || 300),
                distinctUntilChanged(),
                takeUntil(this.destroy$)
            )
            .subscribe((query) => {
                this.handleSearch(query);
            });
    }

    ngOnInit(): void {
        // Initialize component
        // Loading state is now handled by computed signal
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Handle complete/search event
     */
    handleComplete(event: any): void {
        const query = event.query || '';
        this.searchQuery.set(query);

        // Emit the complete event
        this.onComplete.emit({
            query,
            originalEvent: event.originalEvent,
        });
    }

    /**
     * Handle search (debounced)
     */
    private handleSearch(query: string): void {
        this.onComplete.emit({
            query,
        });
    }

    /**
     * Handle select event
     */
    handleSelect(event: any): void {
        this.onSelect.emit({
            value: event.value,
            originalEvent: event.originalEvent,
        });
    }

    /**
     * Handle unselect event (for multiple mode)
     */
    handleUnselect(event: any): void {
        this.onUnselect.emit({
            value: event.value,
            originalEvent: event.originalEvent,
        });
    }

    /**
     * Handle value change from ngModel
     */
    onValueChange(newValue: any): void {
        const config = this.autoCompleteConfig();
        let finalValue = newValue;

        // For multiple mode, ensure array
        if (config.multiple) {
            finalValue = Array.isArray(newValue) ? newValue : (newValue ? [newValue] : []);
        }

        // Update the internal value
        this.value.set(finalValue);

        // Notify Angular forms of the change
        this.onChangeFn(finalValue);
        this.onTouchedFn();

        // Emit the change event
        this.onChange.emit({
            value: finalValue,
            originalEvent: undefined,
        });
    }

    /**
     * Handle focus event
     */
    handleFocus(): void {
        this.onTouchedFn();
        this.onFocus.emit();
    }

    /**
     * Handle blur event
     */
    handleBlur(): void {
        this.onBlur.emit();
    }

    /**
     * Handle key up event
     */
    handleKeyUp(event: KeyboardEvent): void {
        this.onKeyUp.emit(event);
    }

    /**
     * Handle show event
     */
    handleShow(): void {
        this.onShow.emit();
    }

    /**
     * Handle hide event
     */
    handleHide(): void {
        this.onHide.emit();
    }

    /**
     * Handle clear event
     */
    handleClear(event?: any): void {
        this.searchQuery.set('');
        this.value.set(this.autoCompleteConfig().multiple ? [] : null);
        this.onClear.emit({
            originalEvent: event,
        });
    }

    /**
     * Handle dropdown click event
     */
    handleDropdownClick(event: any): void {
        this.onDropdownClick.emit({
            query: this.searchQuery(),
            originalEvent: event.originalEvent,
        });
    }

    /**
     * Handle lazy load event
     */
    handleLazyLoad(event: any): void {
        this.internalLoading.set(true);
        this.onLazyLoad.emit(event);
    }

    /**
     * Public method to set loading state
     */
    setLoading(loading: boolean): void {
        this.internalLoading.set(loading);
    }

    /**
     * Public method to clear search
     */
    clearSearch(): void {
        this.searchQuery.set('');
    }

    /**
     * Public method to show the overlay panel
     */
    show(): void {
        if (this.autoCompleteComponent) {
            this.autoCompleteComponent.show();
        }
    }

    /**
     * Public method to hide the overlay panel
     */
    hide(): void {
        if (this.autoCompleteComponent) {
            this.autoCompleteComponent.hide();
        }
    }

    /**
     * Check if option is disabled
     */
    isOptionDisabled(option: AutoCompleteOption): boolean {
        return option.disabled || false;
    }

    // ControlValueAccessor methods
    /**
     * Write a new value to the element (called by Angular forms)
     */
    writeValue(value: any): void {
        const config = this.autoCompleteConfig();
        if (config.multiple) {
            const newValue = Array.isArray(value) ? value : (value ? [value] : []);
            this.value.set(newValue);
        } else {
            this.value.set(value);
        }
    }

    /**
     * Register a callback function to be called when the control's value changes
     */
    registerOnChange(fn: (value: any) => void): void {
        this.onChangeFn = fn;
    }

    /**
     * Register a callback function to be called when the control is touched
     */
    registerOnTouched(fn: () => void): void {
        this.onTouchedFn = fn;
    }

    /**
     * Set the disabled state of the control
     */
    setDisabledState(isDisabled: boolean): void {
        // Note: The disabled state should be managed through the config input
        // This method is required by ControlValueAccessor interface
        // The parent component should update the config.disabled property
    }
}

